// frontend/src/modules/invoicing/types.ts

export type InvoiceStatus =
  | 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled'
  | 'partial' | 'accepted' | 'declined';
export type DocumentType = 'invoice' | 'estimate' | 'credit_note';
export type Cadence = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface Client {
  id: string;
  tenant_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  billing_address: string | null;
  tax_id: string | null;
  notes: string | null;
  status: 'active' | 'archived';
  /** Per-client currency (036). null = workspace default. */
  currency?: string | null;
  /**
   * Marketing-consent record (078). Null = no consent on file, and the client
   * is EXCLUDED from every email campaign — GDPR requires the sender to be able
   * to demonstrate consent, so this is never assumed.
   */
  marketing_consent_at?: string | null;
  marketing_consent_source?: string | null;
  marketing_consent_by_user_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateClientInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  billing_address?: string | null;
  tax_id?: string | null;
  notes?: string | null;
  currency?: string | null;
  /** `true` records consent as of now; `null` revokes it. */
  marketing_consent_at?: string | boolean | null;
  marketing_consent_source?: string | null;
}

export interface Service {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  default_unit_price: string;
  default_tax_rate: string;
  unit: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateServiceInput {
  name: string;
  description?: string | null;
  default_unit_price?: number;
  default_tax_rate?: number;
  unit?: string;
}

export interface InvoiceItem {
  id: string;
  invoice_id: string;
  position: number;
  service_id: string | null;
  description: string;
  quantity: string;
  unit_price: string;
  tax_rate: string;
  subtotal: string;
  tax_amount: string;
  line_total: string;
  /** 036: line discount %, null when none. */
  discount_percent?: string | null;
}

export interface Invoice {
  id: string;
  tenant_id: string;
  invoice_number: string;
  client_id: string;
  status: InvoiceStatus;
  issued_at: string;
  due_at: string;
  sent_at: string | null;
  paid_at: string | null;
  cancelled_at: string | null;
  subtotal: string;
  tax_total: string;
  total: string;
  notes: string | null;
  terms: string | null;
  recurring_invoice_id: string | null;
  created_at: string;
  updated_at: string;
  // ── 036 fields ──
  document_type?: DocumentType;
  amount_paid?: string;
  amount_credited?: string;
  discount_type?: 'percent' | 'fixed' | null;
  discount_value?: string | null;
  tax_profile_id?: string | null;
  converted_from_id?: string | null;
  related_invoice_id?: string | null;
  /** Currency snapshot; null = workspace default. */
  currency?: string | null;
}

export interface InvoiceWithItems extends Invoice {
  items: InvoiceItem[];
}

export interface CreateInvoiceItemInput {
  service_id?: string | null;
  description: string;
  quantity: number;
  unit_price: number;
  tax_rate?: number;
  discount_percent?: number | null;
}

export interface CreateInvoiceInput {
  client_id: string;
  issued_at?: string;
  due_at: string;
  notes?: string | null;
  terms?: string | null;
  items: CreateInvoiceItemInput[];
  document_type?: 'invoice' | 'estimate';
  discount_type?: 'percent' | 'fixed' | null;
  discount_value?: number | null;
  tax_profile_id?: string | null;
}

export interface InvoicePayment {
  id: string;
  tenant_id: string;
  invoice_id: string;
  amount: string;
  method: string | null;
  note: string | null;
  paid_at: string;
  created_at: string;
  created_by_user_id: string | null;
}

export interface TaxProfile {
  id: string;
  tenant_id: string;
  name: string;
  rate_percent: string;
  is_default: boolean;
  created_at: string;
}

export interface RecurringInvoice {
  id: string;
  tenant_id: string;
  name: string;
  client_id: string;
  cadence: Cadence;
  interval_count: number;
  next_run_at: string;
  last_run_at: string | null;
  end_at: string | null;
  auto_send: boolean;
  payment_terms_days: number;
  template_items: CreateInvoiceItemInput[];
  notes_template: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateRecurringInput {
  name: string;
  client_id: string;
  cadence: Cadence;
  interval_count: number;
  next_run_at: string;
  end_at?: string | null;
  auto_send?: boolean;
  payment_terms_days?: number;
  template_items: CreateInvoiceItemInput[];
  notes_template?: string | null;
}

/** Effective amount still due on an invoice (total − paid − credited). */
export function amountDue(inv: Pick<Invoice, 'total' | 'amount_paid' | 'amount_credited'>): number {
  const due = Number(inv.total) - Number(inv.amount_paid ?? 0) - Number(inv.amount_credited ?? 0);
  return Math.max(Math.round((due + Number.EPSILON) * 100) / 100, 0);
}
